import { useEffect, useState, type RefObject, type UIEvent } from 'react';

import { createResizeObserverWithRAF } from '@/utils/resizeObserverWithRAF';

import { checkIsScrolledToBottom, checkIsScrolledToTop } from './utils';

const checkHasOverflow = (el: HTMLElement) => el.scrollHeight > el.clientHeight;

/**
 * Hook to manage gradient visibility based on scroll position.
 * Shows top gradient when scrolled down and bottom gradient when more content exists below.
 */
export const useScrollGradient = (
  ref: RefObject<HTMLElement | null>,
  deps: unknown[] = []
) => {
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  const updateGradients = (el: HTMLElement) => {
    const hasOverflow = checkHasOverflow(el);
    setShowTopGradient(hasOverflow && !checkIsScrolledToTop(el));
    setShowBottomGradient(hasOverflow && !checkIsScrolledToBottom(el));
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    updateGradients(el);

    const observer = createResizeObserverWithRAF(() => updateGradients(el));
    observer.observe(el);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);

  const handleScroll = (event: UIEvent<HTMLElement>) => {
    updateGradients(event.currentTarget);
  };

  return { showTopGradient, showBottomGradient, handleScroll };
};
